import React, { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { matchPath } from 'react-router-dom';
import { AccessService } from '../../service/api/access-service';

/**
 * Permission snapshot from GET /api/access/me (server resolves ALL Entra roles as a union of grants).
 *
 * This is UX only: menus, route guard, Hide/Disable of buttons. The API enforces every permission
 * itself, so a stale or tampered snapshot can never grant access to data or actions.
 */

const SNAPSHOT_KEY = 'RBAC_ACCESS_SNAPSHOT';

const PermissionContext = createContext(null);

const readSnapshot = accountId => {
  try {
    const raw = sessionStorage.getItem(SNAPSHOT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.accountId === accountId ? parsed.me : null;
  } catch (_e) {
    return null;
  }
};

const writeSnapshot = (accountId, me) => {
  try {
    sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify({ accountId, me }));
  } catch (_e) {
    // storage may be unavailable; the snapshot is only a refresh convenience
  }
};

/** Call on logout / account switch (FR-022). */
export const clearAccessSnapshot = () => {
  try {
    sessionStorage.removeItem(SNAPSHOT_KEY);
  } catch (_e) {
    // ignore
  }
};

export const PermissionProvider = ({ children }) => {
  const { accounts } = useMsal();
  const accountId = accounts && accounts.length > 0 ? accounts[0].homeAccountId : null;

  const [me, setMe] = useState(() => (accountId ? readSnapshot(accountId) : null));
  const [status, setStatus] = useState(me ? 'ready' : 'idle'); // idle | loading | ready | error
  const [error, setError] = useState(null);
  const inflight = useRef(null);

  const load = useCallback(async () => {
    if (!accountId) return null;
    if (inflight.current) return inflight.current;

    setStatus(prev => (prev === 'ready' ? 'ready' : 'loading'));
    inflight.current = AccessService.getMe()
      .then(result => {
        if (!result) throw new Error('Empty access response');
        setMe(result);
        setError(null);
        setStatus('ready');
        writeSnapshot(accountId, result);
        return result;
      })
      .catch(err => {
        setError(err);
        setStatus('error');
        return null;
      })
      .finally(() => {
        inflight.current = null;
      });
    return inflight.current;
  }, [accountId]);

  // Load (or reload) whenever the signed-in account changes; drop another account's snapshot.
  useEffect(() => {
    if (!accountId) {
      setMe(null);
      setStatus('idle');
      return;
    }
    const cached = readSnapshot(accountId);
    if (cached) {
      setMe(cached);
      setStatus('ready');
    } else {
      clearAccessSnapshot();
      setMe(null);
    }
    // Always revalidate in the background; the cached copy only avoids a blank screen on refresh.
    load();
  }, [accountId, load]);

  const value = useMemo(() => {
    const permissions = new Set(me?.permissions || []);
    const deniedBehavior = me?.deniedBehavior || {};
    const routes = me?.routes || [];
    const protectedRoutes = me?.protectedRoutes || [];

    const can = permissionKey => {
      if (!permissionKey || !me) return false;
      return me.isSuperAdmin || permissions.has(permissionKey);
    };

    const canAny = keys => (keys || []).some(can);

    /** 'Hide' (default) or 'Disable' — what to do with a control when permission is missing. */
    const behavior = permissionKey => deniedBehavior[permissionKey] || 'Hide';

    /**
     * Route check for the UI guard. Routes that are not in the catalog are not managed by RBAC
     * (e.g. /profile, /notifications) and are allowed for any signed-in user.
     */
    const checkRoute = pathname => {
      const isProtected = protectedRoutes.some(pattern => matchPath({ path: pattern, end: true }, pathname));
      if (!isProtected) return { isProtected: false, allowed: true };
      const allowed = routes.some(r => matchPath({ path: r.route, end: true }, pathname));
      return { isProtected: true, allowed };
    };

    return {
      status,
      error,
      me,
      roles: me?.roles || [],
      isSuperAdmin: !!me?.isSuperAdmin,
      menu: me?.menu || [],
      landingRoute: me?.landingRoute || null,
      can,
      canAny,
      behavior,
      checkRoute,
      reload: load,
    };
  }, [me, status, error, load]);

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};

export const usePermission = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermission must be used within a PermissionProvider');
  }
  return context;
};

/**
 * Renders children only when the permission is granted.
 *   <Can perform="MASTERS.VENDOR.CREATE"><button>Add</button></Can>
 */
export const Can = ({ perform, children, fallback = null }) => {
  const { can } = usePermission();
  return <>{can(perform) ? children : fallback}</>;
};
